import dayjs from 'dayjs';

/**
 * Serialize a calendar date to a bare `YYYY-MM-DD` (DateOnly) string using the
 * value's LOCAL calendar fields.
 *
 * Do NOT use `Date.toISOString().split('T')[0]` (or `.slice(0, 10)`) for this.
 * `toISOString()` converts to UTC first, so a value sitting at local midnight in
 * a UTC-ahead timezone (e.g. the UK during BST, UTC+1) rolls back to the
 * previous day — a user who picks 31 Jul ends up storing/rendering 30 Jul.
 * `dayjs(...).format('YYYY-MM-DD')` reads the local calendar fields directly, so
 * the chosen day is preserved regardless of the browser timezone.
 *
 * Accepts a Dayjs, JS Date, or ISO string. Returns `undefined` for
 * null/undefined/invalid input so callers can pass the result straight through
 * to an optional request field.
 */
export const toDateOnly = (
  value: dayjs.Dayjs | Date | string | null | undefined
): string | undefined => {
  if (value == null) return undefined;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : undefined;
};

/**
 * Read a DateOnly value that came back from the API into a LOCAL-midnight Dayjs.
 *
 * The inverse hazard to `toDateOnly`. `DateReviver` turns a bare `YYYY-MM-DD`
 * response field into `new Date('YYYY-MM-DD')`, which is midnight **UTC** — so
 * `dayjs(thatDate)` in any timezone west of Greenwich reads as the previous
 * day. Rendering it shows the wrong date, and round-tripping it back through
 * `toDateOnly` (which reads local fields) silently sends the wrong date to the
 * server.
 *
 * So: for a `Date`, take its UTC calendar fields and rebuild at local midnight.
 * Strings and Dayjs values are already local-midnight when they represent a
 * calendar date, and are passed through.
 *
 * Only for DATE-ONLY fields. A full timestamp (`createdAt`, `timestamp`) is a
 * real instant — reading its UTC fields as local would shift it.
 */
export const fromDateOnly = (value: dayjs.Dayjs | Date | string): dayjs.Dayjs =>
  value instanceof Date
    ? dayjs(new Date(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate()))
    : dayjs(value);
